import * as Vue from 'vue'
import { defineAsyncComponent, type Component } from 'vue'
import { max, min } from 'lodash'

export const SATELLITE_URL =
  'https://core-sat.maps.yandex.net/tiles?l=sat&lang=ru_RU&x={{x}}&y={{y}}&z={{z}}&scale={{scale}}&projection=web_mercator'
export const ROADS_URL =
  'https://core-renderer-tiles.maps.yandex.net/tiles?l=skl&lang=ru_RU&x={{x}}&y={{y}}&z={{z}}&scale={{scale}}&projection=web_mercator'

export const YMAPS_API_KEY =
  import.meta.env.VITE_YMAPS_API_KEY ?? '53b42425-849b-4dcb-97ef-6305bff7757a'

const YMAPS_SCRIPT_SELECTOR = 'script[data-ymaps3="true"]'
const YMAPS_CLUSTERER_CDN = 'https://cdn.jsdelivr.net/npm/{package}'
const YMAPS_CLUSTERER_PACKAGE = '@yandex/ymaps3-clusterer@latest'

type Ymaps3Global = {
  ready: Promise<void>
  import: {
    (packageName: string): Promise<any>
    registerCdn: (urlTemplate: string, packageName: string) => void
  }
  YMapListener: unknown
}

type YmapsComponents = {
  YMap: Component
  YMapDefaultSchemeLayer: Component
  YMapDefaultFeaturesLayer: Component
  YMapMarker: Component
  YMapControls: Component
  YMapListener: Component
  YMapCollection: Component
  YMapFeature: Component
  YMapContainer: Component
  YMapTileDataSource: Component
  YMapLayer: Component
  YMapClusterer: Component
  clusterByGrid: (...args: any[]) => any
  MapListener: unknown
}

type YmapsComponentName = Exclude<keyof YmapsComponents, 'clusterByGrid' | 'MapListener'>

declare global {
  interface Window {
    ymaps3?: Ymaps3Global
  }
}

let scriptPromise: Promise<void> | null = null
let initPromise: Promise<YmapsComponents> | null = null

export const mapUtil = {
  /**
   * Метод для получения матрицы координат
   * для вычисления центра и масштаба карты.
   *
   * @param longitudeList — список долгот
   * @param latitudeList — список широт
   * @returns границы в формате [[minLng, minLat], [maxLng, maxLat]]
   */
  getBounds(longitudeList: number[], latitudeList: number[]) {
    const maxLn = max(longitudeList)
    const minLn = min(longitudeList)
    const maxLt = max(latitudeList)
    const minLt = min(latitudeList)

    return [
      [minLn, minLt],
      [maxLn, maxLt],
    ]
  },
}

/**
 * Загружает Yandex Maps JS API v3 без блокировки старта приложения.
 * Повторные вызовы переиспользуют один и тот же promise и script-tag.
 *
 * @param apiKey — ключ Yandex Maps API
 * @returns promise завершения загрузки script
 */
export const loadYmapsScript = (apiKey = YMAPS_API_KEY): Promise<void> => {
  if (window.ymaps3) return Promise.resolve()
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(YMAPS_SCRIPT_SELECTOR)

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true })
      existingScript.addEventListener(
        'error',
        () => {
          scriptPromise = null
          reject(new Error('Yandex Maps script failed to load'))
        },
        { once: true },
      )
      return
    }

    const script = document.createElement('script')
    script.src = `https://api-maps.yandex.ru/v3/?apikey=${apiKey}&lang=ru_RU`
    script.async = true
    script.dataset.ymaps3 = 'true'
    script.onload = () => resolve()
    script.onerror = () => {
      script.remove()
      scriptPromise = null
      reject(new Error('Yandex Maps script failed to load'))
    }

    document.head.appendChild(script)
  })

  return scriptPromise
}

/**
 * Инициализирует Vue-компоненты Yandex Maps и clusterer только при первом реальном использовании.
 * Ошибка инициализации сбрасывает cache, чтобы следующая попытка могла повторить загрузку.
 *
 * @returns набор Vue-компонентов и runtime helpers Yandex Maps
 */
export const initYmapsComponents = (): Promise<YmapsComponents> => {
  if (initPromise) return initPromise

  initPromise = (async () => {
    await loadYmapsScript()

    const ymaps3 = window.ymaps3
    if (!ymaps3) {
      throw new Error('Yandex Maps global object is not available')
    }

    await ymaps3.ready
    ymaps3.import.registerCdn(YMAPS_CLUSTERER_CDN, YMAPS_CLUSTERER_PACKAGE)

    const [ymaps3Vue, ymaps3Clusterer] = await Promise.all([
      ymaps3.import('@yandex/ymaps3-vuefy'),
      ymaps3.import('@yandex/ymaps3-clusterer'),
    ])

    const vuefy = ymaps3Vue.vuefy.bindTo(Vue)
    const baseModule = vuefy.module(ymaps3)
    const clustererModule = vuefy.module(ymaps3Clusterer)

    return {
      YMap: baseModule.YMap,
      YMapDefaultSchemeLayer: baseModule.YMapDefaultSchemeLayer,
      YMapDefaultFeaturesLayer: baseModule.YMapDefaultFeaturesLayer,
      YMapMarker: baseModule.YMapMarker,
      YMapControls: baseModule.YMapControls,
      YMapListener: baseModule.YMapListener,
      YMapCollection: baseModule.YMapCollection,
      YMapFeature: baseModule.YMapFeature,
      YMapContainer: baseModule.YMapContainer,
      YMapTileDataSource: baseModule.YMapTileDataSource,
      YMapLayer: baseModule.YMapLayer,
      YMapClusterer: clustererModule.YMapClusterer,
      clusterByGrid: clustererModule.clusterByGrid,
      MapListener: ymaps3.YMapListener,
    }
  })().catch((error) => {
    initPromise = null
    throw error
  })

  return initPromise
}

/**
 * Создаёт async Vue-компонент, который подтягивает Yandex Maps только при рендере.
 *
 * @param name — имя компонента из результата initYmapsComponents
 * @returns Vue async component
 */
const asyncYmapComponent = (name: YmapsComponentName): Component =>
  defineAsyncComponent(async () => {
    const components = await initYmapsComponents()

    return components[name]
  })

export const YMap = asyncYmapComponent('YMap')
export const YMapDefaultSchemeLayer = asyncYmapComponent('YMapDefaultSchemeLayer')
export const YMapDefaultFeaturesLayer = asyncYmapComponent('YMapDefaultFeaturesLayer')
export const YMapMarker = asyncYmapComponent('YMapMarker')
export const YMapControls = asyncYmapComponent('YMapControls')
export const YMapListener = asyncYmapComponent('YMapListener')
export const YMapCollection = asyncYmapComponent('YMapCollection')
export const YMapFeature = asyncYmapComponent('YMapFeature')
export const YMapContainer = asyncYmapComponent('YMapContainer')
export const YMapTileDataSource = asyncYmapComponent('YMapTileDataSource')
export const YMapLayer = asyncYmapComponent('YMapLayer')
export const YMapClusterer = asyncYmapComponent('YMapClusterer')

/**
 * Возвращает Yandex clusterByGrid после ленивой инициализации clusterer-пакета.
 *
 * @returns функция clusterByGrid из @yandex/ymaps3-clusterer
 */
export const getClusterByGrid = async () => (await initYmapsComponents()).clusterByGrid

/**
 * Возвращает runtime-конструктор YMapListener после ленивой инициализации API.
 *
 * @returns YMapListener из глобального ymaps3
 */
export const getMapListener = async () => (await initYmapsComponents()).MapListener
